import { useMemo, useRef, useState, useEffect } from 'react'
import {
  GridContainer,
  Button,
  TemplateBadges,
  MonochromeIcon,
  TemplateItem,
  TemplateDetailsBar,
  TemplateImagePreview,
  TemplateColorsPallette,
  TemplateColor,
  HeaderTitle,
  PickButtonContainer,
  TemplateNameLink,
  SpinnerWrapper,
  StickyCategoryContainer,
} from './styles'
import { i18n as I18n } from 'builder/utils/i18n'
import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { withMediaQueries } from 'builder/components/MediaQueries'
import TemplatesCategories from './TemplatesCategories'
import Badges from 'builder/components/Badges'
import PropTypes from 'prop-types'
import { DocumentTypes } from 'builder/modules/constants'
import Spinner from 'builder/components/Spinner'
import { failsafeLoadImg } from './Templates'
import { debounce } from 'lodash'
import { createPortal } from 'react-dom'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors } from 'builder/modules/init'

const gridConfig = {
  mobile: 2,
  desktop: 4,
}

const FIRST_IMAGES_TO_PRELOAD_COUNT = 8

// List of locales for EU countries + Switzerland (de-CH)
const EU_COUNTRIES_LOCALES = ["bg", "nl-NL", "es-ES", "sv-SE", "de-AT", "de-DE", "fr-FR", "pt-PT", "en-IE", "it", "cs", "fi", "ro", "hu", "gr", "pl", "da", "de-CH"]


function Template({ template, onChoose, documentType, rtpAtpUnhideCtaAB, atpStickyCategories, showPremiumBadge }) {
  const { id, thumbnail, imagesWithColors, premium, supportedFormats, name } = template
  const imgRef = useRef()

  const setColor = (color, url) => {
    imgRef.current.src = url
  }

  const handleChooseClick = (id, name) => {
    onChoose(id)
    trackInternalEvent(`choose_sign_up_${documentType}_template`, {
      template: id,
      label: 'funnel',
      stickyCategory: atpStickyCategories ? 'yes' : 'no',
    })
    trackMarketingEvent('Sign Up', name)
  }

  return (
    <TemplateItem data-testid={`${id}-template`}>
      <TemplateImagePreview ref={imgRef} loading="lazy" alt={`${id} template`} src={thumbnail} />
      <PickButtonContainer onClick={() => handleChooseClick(id, name)}>
        <Button visible={rtpAtpUnhideCtaAB === 'v1' ? true : undefined}>
          {I18n.t('builder.sign_up.select')}
        </Button>
      </PickButtonContainer>
      <TemplateDetailsBar>
        <TemplateColorsPallette>
          {imagesWithColors?.length > 1 ? (
            imagesWithColors.map(({ color, url, default: isDefault }) => (
              <TemplateColor
                onMouseEnter={() => setColor(color, url)}
                key={color}
                isActive={false}
                color={color}
              />
            ))
          ) : (
            <MonochromeIcon />
          )}
        </TemplateColorsPallette>
        <TemplateBadges>
          {!premium && <Badges badges={[I18n.t('builder.templates.free_plan')]} />}
          {showPremiumBadge && premium && <Badges badges={[I18n.t('builder.templates.premium_plan')]} />}
          <Badges badges={supportedFormats} />
        </TemplateBadges>
      </TemplateDetailsBar>
      <TemplateNameLink onClick={() => handleChooseClick(id, name)}>{name}</TemplateNameLink>
    </TemplateItem>
  )
}

Template.propTypes = {
  template: PropTypes.object.isRequired,
  onChoose: PropTypes.func.isRequired,
  documentType: PropTypes.oneOf(Object.values(DocumentTypes)),
  rtpAtpUnhideCtaAB: PropTypes.oneOf(['v0', 'v1']),
  atpStickyCategories: PropTypes.bool,
  showPremiumBadge: PropTypes.bool,
}

function TemplatesV2({
  templates = [],
  onChoose,
  documentType,
  categories = [],
  loading = false,
  rtpAtpUnhideCtaAB = 'v0',
  atpStickyCategories = false,
}) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeCategory, setActiveCategory] = useState({ slug: 'all' })
  const [isLoaded, setIsLoaded] = useState(false)
  const locale = useTypedSelector(selectors.locale)

  useEffectOnMount(() => {
    const handleScroll = debounce(() => {
      const scrollPosition = window.scrollY

      if (scrollPosition > 300) setIsScrolled(true)
      else setIsScrolled(false)
    }, 100)

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const filteredTemplates = useMemo(() => {
    if (!activeCategory?.templates || activeCategory?.slug === 'all') return templates
    return templates.filter(template => activeCategory.templates.includes(template.id))
  }, [activeCategory, templates])

  const { mobile: mobileColumns, desktop: desktopColumns } = gridConfig

  useEffect(() => {
    if (!filteredTemplates.length || isLoaded) return
    const imagesToPreload = filteredTemplates
      .slice(0, FIRST_IMAGES_TO_PRELOAD_COUNT)
      .map(({ thumbnail }) => failsafeLoadImg(thumbnail))

    Promise.all(imagesToPreload).then(() => {
      setIsLoaded(true)
    })
  }, [filteredTemplates])

  useEffect(() => {
    if (window.location.pathname === '/app/create-resume/templates') {
      trackMarketingEvent('Application', `visit_app_${documentType}_templates_page`)
      trackInternalEvent(`visit_app_${documentType}_templates_page`)
    } else if (window.location.pathname === '/app/create-cover-letter/templates') {
      trackInternalEvent(`visit_app_${documentType}_templates_page`)
    }
  }, [documentType])

  return (
    <>
      {atpStickyCategories &&
        createPortal(
          <StickyCategoryContainer visible={isScrolled}>
            <TemplatesCategories
              activeCategory={activeCategory}
              categories={categories}
              setActiveCategory={setActiveCategory}
              documentType={documentType}
              i18n={I18n}
              atpStickyCategories={atpStickyCategories}
            />
          </StickyCategoryContainer>,
          document.body,
        )}
      <HeaderTitle>{I18n.t('builder.sign_up.select_template')}</HeaderTitle>

      {loading && (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      )}
      {!loading && (
        <>
          <TemplatesCategories
            activeCategory={activeCategory}
            categories={categories}
            setActiveCategory={setActiveCategory}
            documentType={documentType}
            i18n={I18n}
          />
          {!isLoaded ? (
            <SpinnerWrapper data-testid="spinner">
              <Spinner />
            </SpinnerWrapper>
          ) : (
            <GridContainer mobileColumns={mobileColumns} desktopColumns={desktopColumns}>
              {filteredTemplates.map(template => (
                <Template
                  key={template.id}
                  template={template}
                  onChoose={onChoose}
                  documentType={documentType}
                  rtpAtpUnhideCtaAB={rtpAtpUnhideCtaAB}
                  showPremiumBadge={EU_COUNTRIES_LOCALES.includes(locale)}
                />
              ))}
            </GridContainer>
          )}
        </>
      )}
    </>
  )
}

TemplatesV2.propTypes = {
  templates: PropTypes.array.isRequired,
  onChoose: PropTypes.func.isRequired,
  mediaQueries: PropTypes.object.isRequired,
  recommended: PropTypes.string,
  currentTemplate: PropTypes.string,
  documentType: PropTypes.oneOf(Object.values(DocumentTypes)),
  categories: PropTypes.array,
  variant: PropTypes.string,
  loading: PropTypes.bool,
  rtpAtpUnhideCtaAB: PropTypes.oneOf(['v0', 'v1']),
  atpStickyCategories: PropTypes.bool,
}

export default withMediaQueries(TemplatesV2)
